import React, { useEffect, useState } from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TextField, TablePagination,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Checkbox, MenuItem, Select, InputLabel,
    Alert, Snackbar, CircularProgress
} from "@mui/material";
import {
    getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, limit
} from "firebase/firestore";
import Payroleheader from "./Payroleheader";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { startOfMonth, endOfMonth } from "date-fns"; // optional for better date handling

const db = getFirestore();

function Staffmonthlypayment() {
    const [data, setData] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [search, setSearch] = useState("");
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);

    // pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // modal
    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({
        staffid: "",
        totalDays: 0,
        presentDays: 0,
        overtimeDays: 0,
        baseSalary: 0,
        advanceDeducted: 0,
        netSalary: 0,
        paidSalary: 0,
        remainingSalary: 0,
        payby: "cash",
        paydate: new Date().toISOString().split("T")[0],
        deductAdvanceNow: false,
        year: new Date().getFullYear().toString(),
        month: (new Date().getMonth() + 1).toString().padStart(2, '0')
    });

    const [lastMonthRemainingSalary, setLastMonthRemainingSalary] = useState(0);
    const [totalPendingAdvance, setTotalPendingAdvance] = useState(0);

    // Snackbar for alerts
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    // fetch staff details
    useEffect(() => {
        const fetchStaff = async () => {
            const snap = await getDocs(collection(db, "staffdetails"));
            setStaffList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        };
        fetchStaff();
    }, []);

    // fetch monthly salary records
    const fetchData = async () => {
        setLoading(true);
        try {
            const paymentsCollection = collection(db, "staffpayments");
            const q = query(paymentsCollection, orderBy("paydate", "desc"));
            const snap = await getDocs(q);
            setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching payments:", err);
            setAlert({ open: true, message: "Error fetching payments.", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);


    const handleSalaryCalculation = async () => {
        if (!formData.staffid) {
            setAlert({ open: true, message: "कृपया कर्मचारी निवडा.", severity: "warning" });
            return;
        }

        const staff = staffList.find((s) => s.id === formData.staffid);
        if (!staff) return;

        const formatDate = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        // Get current month and year from paydate
        const payDate = new Date(formData.paydate);

        const monthStart = new Date(payDate.getFullYear(), payDate.getMonth(), 1);
        const monthEnd = new Date(payDate.getFullYear(), payDate.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);

        const startDate = formatDate(monthStart);
        const endDate = formatDate(monthEnd);

        const totalDays = monthEnd.getDate();

        // Fetch attendance for selected month
        const attendanceSnap = await getDocs(
            query(
                collection(db, "attendance"),
                where("staffId", "==", formData.staffid),
                where("date", ">=", startDate),
                where("date", "<=", endDate)
            )
        );

        let presentDays = 0;
        const uniqueDates = new Set();  // ⬅️ For distinct dates

        attendanceSnap.docs.forEach(d => {
            const data = d.data();

            // Add to distinct date set
            uniqueDates.add(data.date);

            // Count present / half-day
            if (data.status === "Present") {
                presentDays += 1;
            } else if (data.status === "Half Day") {
                presentDays += 0.5;
            }
        });

        // Convert to array if needed
        const distinctDatesArray = Array.from(uniqueDates);

        // Round to 2 decimals if needed
        presentDays = Number(presentDays.toFixed(2));

        // Overtime days already in formData
        const overtimeDays = Number(formData.overtimeDays) || 0;

        // Per day salary
        const perDaySalary = totalDays > 0 ? Number(staff.monthlypayment) / totalDays : 0;
        const baseSalary = perDaySalary * (presentDays + overtimeDays);

        // Fetch last month remaining & pending advance (existing logic)
        let lastMonthRemaining = 0;
        let pendingAdvances = 0;

        const paymentsQuery = query(
            collection(db, "staffpayments"),
            where("staffid", "==", formData.staffid),
            orderBy("paydate", "desc"),
            limit(1)
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        if (!paymentsSnap.empty) lastMonthRemaining = Number(paymentsSnap.docs[0].data().remainingSalary) || 0;

        const advancesSnap = await getDocs(
            query(
                collection(db, "staffadvance"),
                where("staffid", "==", formData.staffid),
                where("status", "==", "No"),
                where("date", ">=", startDate),
                where("date", "<=", endDate)
            )
        );
        pendingAdvances = advancesSnap.docs.reduce((sum, a) => sum + (parseFloat(a.data().advanceamount) || 0), 0);

        const totalOwed = baseSalary + lastMonthRemaining;
        // const advanceDeducted = baseSalary;
        const advanceDeducted = formData.deductAdvanceNow ? pendingAdvances : 0;

        const netSalary = totalOwed - advanceDeducted;
        const paidSalary = Math.max(0, netSalary);
        const remainingSalary = netSalary - paidSalary;

        // Update state
        setFormData(prev => ({
            ...prev,
            totalDays,
            presentDays,
            baseSalary: baseSalary.toFixed(2),
            advanceDeducted: advanceDeducted.toFixed(2),
            netSalary: netSalary.toFixed(2),
            paidSalary: paidSalary.toFixed(2),
            remainingSalary: remainingSalary.toFixed(2)
        }));

        setLastMonthRemainingSalary(lastMonthRemaining);
        setTotalPendingAdvance(pendingAdvances + Math.abs(lastMonthRemaining));
    };


    const handleSave = async () => {
        try {
            const payDate = new Date(formData.paydate);
            const year = payDate.getFullYear().toString();
            const month = (payDate.getMonth() + 1).toString().padStart(2, '0');
            const finalData = { ...formData, year, month };

            if (!editId) {
                const q = query(
                    collection(db, "staffpayments"),
                    where("staffid", "==", finalData.staffid),
                    where("year", "==", finalData.year),
                    where("month", "==", finalData.month)
                );
                const existingPaymentsSnap = await getDocs(q);
                if (!existingPaymentsSnap.empty) {
                    setAlert({ open: true, message: "A payment for this staff member already exists for the selected month.", severity: "error" });
                    return;
                }
            }

            // Recalculate remaining salary based on manual input before saving
            const totalDue = Number(finalData.netSalary);
            const amountPaid = Number(finalData.paidSalary);
            finalData.remainingSalary = (totalDue - amountPaid).toFixed(2);

            if (editId) {
                await updateDoc(doc(db, "staffpayments", editId), finalData);
                setAlert({ open: true, message: "Payment updated successfully!", severity: "success" });
            } else {
                await addDoc(collection(db, "staffpayments"), finalData);
                setAlert({ open: true, message: "Payment added successfully!", severity: "success" });
            }

            // Update advances if deducted and advances were actually paid
            if (finalData.deductAdvanceNow && totalPendingAdvance > 0) {
                const advSnap = await getDocs(
                    query(
                        collection(db, "staffadvance"),
                        where("staffid", "==", finalData.staffid),
                        where("status", "==", "No")
                    )
                );
                const updates = advSnap.docs.map((d) =>
                    updateDoc(doc(db, "staffadvance", d.id), { status: "Yes" })
                );
                await Promise.all(updates);
            }

            // Reset form and close dialog
            handleCloseDialog();
            fetchData();
        } catch (err) {
            console.error("Save error:", err);
            setAlert({ open: true, message: "An error occurred while saving the payment.", severity: "error" });
        }
    };

    const handleEdit = (row) => {
        setEditId(row.id);
        setFormData({ ...row });
        setOpen(true);
    };

    const handleDelete = async (row) => {
        try {
            await deleteDoc(doc(db, "staffpayments", row.id));

            // Reverse advance if it was fully deducted as part of this payment
            if (row.deductAdvanceNow) {
                const advSnap = await getDocs(
                    query(
                        collection(db, "staffadvance"),
                        where("staffid", "==", row.staffid),
                        where("status", "==", "Yes")
                    )
                );
                const updates = advSnap.docs.map((d) =>
                    updateDoc(doc(db, "staffadvance", d.id), { status: "No" })
                );
                await Promise.all(updates);
            }

            setAlert({ open: true, message: "Payment deleted successfully!", severity: "success" });
            fetchData();
        } catch (err) {
            console.error("Delete error:", err);
            setAlert({ open: true, message: "An error occurred while deleting the payment.", severity: "error" });
        }
    };

    // filter staff search
    const filteredData = data.filter((row) => {
        const staff = staffList.find((s) => s.id === row.staffid);
        return staff?.staffname?.toLowerCase().includes(search.toLowerCase());
    });

    // Update remaining salary display on paid salary change
    useEffect(() => {
        const totalDue = Number(formData.netSalary);
        const amountPaid = Number(formData.paidSalary);
        const remaining = (totalDue - amountPaid);
        setFormData(prev => ({ ...prev, remainingSalary: (isNaN(remaining) ? 0 : remaining).toFixed(2) }));
    }, [formData.netSalary, formData.paidSalary]);

    // Reset lastMonthRemainingSalary and totalPendingAdvance when dialog is opened for a new record
    useEffect(() => {
        if (open && !editId) {
            setLastMonthRemainingSalary(0);
            setTotalPendingAdvance(0);
        }
    }, [open, editId]);

    // Handle dialog close and reset
    const handleCloseDialog = () => {
        setOpen(false);
        setEditId(null);
        setFormData({
            staffid: "",
            totalDays: 0,
            presentDays: 0,
            overtimeDays: 0,
            baseSalary: 0,
            advanceDeducted: 0,
            netSalary: 0,
            paidSalary: 0,
            remainingSalary: 0,
            payby: "cash",
            paydate: new Date().toISOString().split("T")[0],
            deductAdvanceNow: false,
            year: new Date().getFullYear().toString(),
            month: (new Date().getMonth() + 1).toString().padStart(2, '0')
        });
        setLastMonthRemainingSalary(0);
        setTotalPendingAdvance(0);
    };

    return (
        <Box p={3}>
            <Payroleheader />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                कर्मचारी मासिक पगार
            </Typography>

            <Button variant="contained" onClick={() => setOpen(true)}>
                मासिक पगार जोडा
            </Button>

            <TextField
                label="कर्मचारी शोधा"
                size="small"
                fullWidth
                margin="normal"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>क्रिया</TableCell>
                                <TableCell>कर्मचारी नाव</TableCell>
                                <TableCell>महिना</TableCell>
                                <TableCell>एकूण दिवस</TableCell>
                                <TableCell>हजर दिवस</TableCell>
                                <TableCell>जादा कामाचे दिवस</TableCell>
                                <TableCell>मूळ पगार</TableCell>
                                <TableCell>कपात झालेले आगाऊ</TableCell>
                                <TableCell>एकूण देय</TableCell>
                                <TableCell>दिलेला पगार</TableCell>
                                <TableCell>शिल्लक</TableCell>
                                <TableCell>देयक पद्धत</TableCell>
                                <TableCell>पगार तारीख</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => {
                                    const staff = staffList.find((s) => s.id === row.staffid);

                                    // Construct WhatsApp message
                                    const message = `
कर्मचारी नाव: ${staff?.staffname || "अज्ञात"}
महिना: ${row.month}/${row.year}
एकूण दिवस: ${row.totalDays}
हजर दिवस: ${row.presentDays}
जादा कामाचे दिवस: ${row.overtimeDays}
मूळ पगार: ${row.baseSalary}
कपात झालेले आगाऊ: ${row.advanceDeducted}
एकूण देय: ${row.netSalary}
दिलेला पगार: ${row.paidSalary}
शिल्लक पगार: ${row.remainingSalary}
देयक पद्धत: ${row.payby}
पगार तारीख: ${row.paydate}
      `;
                                    const whatsappLink = `https://wa.me/${staff?.mobile}?text=${encodeURIComponent(message)}`;

                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell width={50}>
                                                <Box display="flex" gap={0.1}>
                                                    <Button
                                                        size="small"
                                                        color="success"
                                                        component="a"
                                                        href={whatsappLink}
                                                        target="_blank"
                                                    >
                                                        <WhatsAppIcon fontSize="small" />
                                                    </Button>

                                                    <Button size="small" onClick={() => handleEdit(row)} color="primary">
                                                        <EditIcon fontSize="small" />
                                                    </Button>

                                                    <Button size="small" color="error" onClick={() => handleDelete(row)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </Button>
                                                </Box>
                                            </TableCell>

                                            <TableCell>{staff?.staffname || "अज्ञात"}</TableCell>
                                            <TableCell>
                                                {row.month && row.year
                                                    ? `${new Date(row.year, row.month - 1).toLocaleString("mr-IN", { month: "long" })} ${row.year}`
                                                    : "-"}
                                            </TableCell>
                                            <TableCell>{row.totalDays}</TableCell>
                                            <TableCell>{row.presentDays}</TableCell>
                                            <TableCell>{row.overtimeDays}</TableCell>
                                            <TableCell>{row.baseSalary}</TableCell>
                                            <TableCell>{row.advanceDeducted}</TableCell>
                                            <TableCell>{row.netSalary}</TableCell>
                                            <TableCell>{row.paidSalary}</TableCell>
                                            <TableCell>{row.remainingSalary}</TableCell>
                                            <TableCell>{row.payby}</TableCell>
                                            <TableCell>{row.paydate}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
            />

            {/* Add/Edit Payment Dialog */}
            <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editId ? "मासिक पगार संपादित करा" : "मासिक पगार जोडा"}
                </DialogTitle>
                <DialogContent>
                    <InputLabel>कर्मचारी</InputLabel>
                    <Select
                        fullWidth
                        size="small"
                        value={formData.staffid}
                        onChange={(e) =>
                            setFormData({ ...formData, staffid: e.target.value })
                        }
                    >
                        {staffList.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.staffname}
                            </MenuItem>
                        ))}
                    </Select>

                    <TextField
                        label="पगाराची तारीख (महिन्यासाठी संदर्भ)"
                        type="date"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={formData.paydate}
                        onChange={(e) =>
                            setFormData({ ...formData, paydate: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="एकूण दिवस"
                        type="number"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={formData.totalDays}
                        onChange={(e) =>
                            setFormData({ ...formData, totalDays: e.target.value })
                        }
                    />
                    <TextField
                        label="हजर दिवस"
                        type="number"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={formData.presentDays}
                        onChange={(e) =>
                            setFormData({ ...formData, presentDays: e.target.value })
                        }
                    />
                    <TextField
                        label="जादा कामाचे दिवस"
                        type="number"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={formData.overtimeDays}
                        onChange={(e) =>
                            setFormData({ ...formData, overtimeDays: e.target.value })
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.deductAdvanceNow}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        deductAdvanceNow: e.target.checked,
                                    })
                                }
                            />
                        }
                        label="या महिन्यात आगाऊ कपात करायची आहे का?"
                    />

                    <Button
                        onClick={handleSalaryCalculation}
                        variant="outlined"
                        sx={{ mt: 2 }}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : "पगार काढा"}
                    </Button>
                    <Typography mt={2}>मूळ पगार: {formData.baseSalary}</Typography>
                    <Typography>मागील महिन्याची शिल्लक: {lastMonthRemainingSalary.toFixed(2)}</Typography>
                    <Typography>बाकी आगाऊ रक्कम: {totalPendingAdvance.toFixed(2)}</Typography>
                    <Typography>कपात झालेले आगाऊ: {formData.advanceDeducted}</Typography>
                    <Typography>एकूण देय: {formData.netSalary}</Typography>
                    <TextField
                        label="दिलेला पगार"
                        type="number"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={formData.paidSalary}
                        onChange={(e) =>
                            setFormData({ ...formData, paidSalary: e.target.value })
                        }
                    />
                    <Typography>शिल्लक पगार: {formData.remainingSalary}</Typography>

                    <TextField
                        label="देयक पद्धत"
                        fullWidth
                        size="small"
                        margin="normal"
                        value={formData.payby}
                        onChange={(e) =>
                            setFormData({ ...formData, payby: e.target.value })
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="error">
                        रद्द करा
                    </Button>
                    <Button onClick={handleSave} variant="contained">
                        जतन करा
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.severity}
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>

    );
}

export default Staffmonthlypayment;